import { createContext, useContext, useState, useCallback } from 'react';
import {
  CheckCircleIcon,
  CloseIcon,
  InfoIcon,
  AlertHexaIcon,
  ErrorHexaIcon,
} from '../icons';

const NotificationContext = createContext(undefined);

let notificationId = 0;

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

const variantStyles = {
  success: {
    borderColor: 'border-success-500',
    iconBg: 'bg-success-50 text-success-500',
    Icon: CheckCircleIcon,
  },
  info: {
    borderColor: 'border-blue-light-500',
    iconBg: 'bg-blue-light-50 text-blue-light-500',
    Icon: InfoIcon,
  },
  warning: {
    borderColor: 'border-warning-500',
    iconBg: 'bg-warning-50 text-warning-500',
    Icon: AlertHexaIcon,
  },
  error: {
    borderColor: 'border-error-500',
    iconBg: 'bg-error-50 text-error-500',
    Icon: ErrorHexaIcon,
  },
};

const NotificationItem = ({ notification, onClose }) => {
  const { borderColor, iconBg, Icon } = variantStyles[notification.variant] || variantStyles.info;

  return (
    <div
      className={`flex items-center justify-between gap-3 w-full rounded-md border-b-4 p-3 shadow-theme-sm bg-white dark:bg-[#1E2634] ${borderColor} animate-slide-in`}
    >
      <div className="flex items-center gap-4">
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${iconBg}`}>
          <Icon className="size-5" />
        </div>
        <div>
          <h4 className="text-sm text-gray-800 dark:text-white/90">{notification.title}</h4>
          {notification.description && (
            <p className="mt-1 text-xs text-gray-600 dark:text-white/70">{notification.description}</p>
          )}
        </div>
      </div>
      <button onClick={() => onClose(notification.id)} className="text-gray-400 hover:text-gray-800 dark:hover:text-white/90 shrink-0">
        <CloseIcon className="size-5" />
      </button>
    </div>
  );
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const notify = useCallback((variant, title, description = '') => {
    const id = ++notificationId;
    const notification = { id, variant, title, description };
    setNotifications((prev) => [...prev, notification]);
    setTimeout(() => removeNotification(id), 4000);
  }, [removeNotification]);

  const toast = {
    success: (title, description) => notify('success', title, description),
    error: (title, description) => notify('error', title, description),
    info: (title, description) => notify('info', title, description),
    warning: (title, description) => notify('warning', title, description),
  };

  return (
    <NotificationContext.Provider value={toast}>
      {children}
      <div className="fixed top-4 right-4 z-999999 flex flex-col gap-2 w-full max-w-[360px]">
        {notifications.map((n) => (
          <NotificationItem key={n.id} notification={n} onClose={removeNotification} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
